import React, { Suspense, useState } from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { TranslateFunction } from 'react-localize-redux';

import { Button } from '../../../../components/atoms/Button';
import { Typography } from '../../../../components/atoms/Typography';
import { Present as PresentIcon } from '../../../../components/icons/Present';
import { LoadingSpinner } from '../../../../components/layouts/LoadingSpinner';
import { Popout } from '../../../../components/layouts/Popout';
import { LoyaltyVoucherDialog } from '../../../../components/partials/LoyaltyVoucherDialog';
import { Voucher } from '../../../../core/state/store/store.types';
import { getBasketErrorMessage } from '../../../../utils/basketUtils';
import { trackVoucherAdded } from '../../../../utils/voucherUtils';
import {
  CollectionOrder,
  Currency,
  DeliveryOrder,
  VoucherBasketItemDm,
} from '../../../../weborder/Api';
import { ToCurrency } from '../../../../weborder/BasketControl';
import {
  BasketError,
  OrderPayload,
} from '../../../../weborder/selectors/basketSelectors';
import { LoyaltyVoucherListDialog } from '../LoyaltyVoucherListDialog';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
  },
  iconContainer: {
    padding: theme.spacing(1.25, 1.5, 0, 0),
  },
  buttonGroup: {
    paddingTop: theme.spacing(1.5),

    '& button:nth-child(2)': {
      marginLeft: theme.spacing(1.5),
    },
  },
}));

export type NewVoucherPopoutProps = {
  checkUserVoucher: (voucher: {
    code: string;
    orderPayload: DeliveryOrder | CollectionOrder | BasketError;
  }) => any;
  currency: Currency;
  languageCode: string;
  orderPayload: OrderPayload;
  pointerVariant?: 'right' | 'center';
  translate: TranslateFunction;
  vouchers: Voucher[];
};

export function NewVoucherPopout(props: NewVoucherPopoutProps): JSX.Element {
  const {
    checkUserVoucher,
    currency,
    languageCode,
    orderPayload,
    pointerVariant = 'right',
    translate,
    vouchers,
  } = props;
  const classes = useStyles();

  const { enqueueSnackbar } = useSnackbar();

  const [showLoyaltyVoucherDialog, setShowLoyaltyVoucherDialog] =
    useState<boolean>(false);
  const [showViewVouchersDialog, setShowViewVouchersDialog] =
    useState<boolean>(false);
  const [selectedVoucher, setSelectedVoucher] = useState<Voucher>(vouchers[0]);
  const [isApplyVoucherLoading, setIsApplyVoucherLoading] =
    useState<boolean>(false);

  const hasMultipleVouchers = vouchers.length > 1;

  const toggleLoyaltyVoucherDialog = (): void =>
    setShowLoyaltyVoucherDialog(!showLoyaltyVoucherDialog);

  const toggleViewVouchersDialog = (): void =>
    setShowViewVouchersDialog(!showViewVouchersDialog);

  const handleShowVoucherDialog = (voucher?: Voucher): void => {
    setShowLoyaltyVoucherDialog(true);
    if (hasMultipleVouchers) {
      setSelectedVoucher(voucher);
    }
  };

  const handleCloseDialogs = (): void => {
    setShowLoyaltyVoucherDialog(false);
    setShowViewVouchersDialog(false);
  };

  const handleOnApplyVoucher = (voucherCode: string): void => {
    setIsApplyVoucherLoading(true);

    const hasBasketError = orderPayload && typeof orderPayload === 'string';
    if (hasBasketError) {
      const basketErrorMessage = getBasketErrorMessage(orderPayload, translate);

      enqueueSnackbar(basketErrorMessage, {
        variant: 'error',
      });
      handleCloseDialogs();
      setIsApplyVoucherLoading(false);
      return;
    }

    checkUserVoucher({ orderPayload, code: voucherCode })
      .then((newVoucher: VoucherBasketItemDm | null) => {
        if (newVoucher) {
          enqueueSnackbar(translate('LoyaltyPopout_voucherAddedMessage'), {
            variant: 'reward',
          });
          trackVoucherAdded(newVoucher);
        } else {
          enqueueSnackbar(translate('Unexpected_error_occurred'), {
            variant: 'error',
          });
        }
      })
      .catch((error: Error) => {
        enqueueSnackbar(error.message, {
          variant: 'error',
        });
      })
      .finally(() => {
        handleCloseDialogs();
        setIsApplyVoucherLoading(false);
      });
  };

  const renderMultipleVouchersPopout = (): JSX.Element => (
    <>
      <div>
        <Typography variant="h3">
          {translate('LoyaltyPopout_multipleVouchersTitle', {
            numberOfVouchers: vouchers.length,
          })}
        </Typography>
        <Typography variant="body2-secondary">
          {translate('LoyaltyPopout_multipleVouchersSubTitle')}
        </Typography>

        <div className={classes.buttonGroup}>
          <Button
            onClick={toggleViewVouchersDialog}
            size="small"
            variant="secondary"
          >
            {translate('LoyaltyPopout_viewVouchers')}
          </Button>
        </div>
      </div>

      {showViewVouchersDialog && (
        <Suspense fallback={<LoadingSpinner />}>
          <LoyaltyVoucherListDialog
            currency={currency}
            isApplyVoucherLoading={isApplyVoucherLoading}
            isDialogOpen={showViewVouchersDialog}
            languageCode={languageCode}
            onApplyVoucher={handleOnApplyVoucher}
            onCloseDialog={toggleViewVouchersDialog}
            onShowVoucherDialog={handleShowVoucherDialog}
            translate={translate}
            vouchers={vouchers}
          />
        </Suspense>
      )}
    </>
  );

  const renderSingleVoucherPopout = (): JSX.Element => {
    const [singleVoucher] = vouchers;

    return (
      <div>
        <Typography variant="h3">
          {translate('Loyalty_loyaltyRewardTitle')}
        </Typography>
        <Typography variant="body2-secondary">
          {translate('Loyalty_loyaltyRewardSubTitle', {
            voucherCode: singleVoucher.code,
            percentage: ToCurrency(
              Number(singleVoucher.discountValue),
              currency,
              languageCode,
            ),
          })}
        </Typography>

        <div className={classes.buttonGroup}>
          <Button
            onClick={(): void => handleOnApplyVoucher(singleVoucher.code)}
            size="small"
            status={isApplyVoucherLoading ? 'loading' : 'default'}
          >
            {translate('LoyaltyPopout_apply')}
          </Button>
          <Button
            variant="secondary"
            size="small"
            onClick={(): void => handleShowVoucherDialog()}
          >
            {translate('LoyaltyPopout_findOutMore')}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Popout
      pointerVariant={pointerVariant}
      popoutName="new-voucher-popout"
      showCloseButton={false}
      showPopout
    >
      <div className={classes.root}>
        <div className={classes.iconContainer}>
          <PresentIcon />
        </div>

        {hasMultipleVouchers
          ? renderMultipleVouchersPopout()
          : renderSingleVoucherPopout()}
        {showLoyaltyVoucherDialog && (
          <Suspense fallback={<LoadingSpinner />}>
            <LoyaltyVoucherDialog
              details={selectedVoucher.description}
              expiry={selectedVoucher.expiryDate}
              isApplyVoucherLoading={isApplyVoucherLoading}
              languageCode={languageCode}
              onApplyVoucher={handleOnApplyVoucher}
              onClose={toggleLoyaltyVoucherDialog}
              open={showLoyaltyVoucherDialog}
              translate={translate}
              voucherCode={selectedVoucher.code}
            />
          </Suspense>
        )}
      </div>
    </Popout>
  );
}
